import type { ReactNode } from "react";
import { Box } from "../../../components/ui/box";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../../../components/ui/empty";
import { Icon, type IconName } from "../../../components/ui/icon";
import type { Omittable } from "../../../lib/props";

// The vendored Empty has no size prop — `sm` compacts the stock paddings via a
// className override so the state sits inside a widget card instead of filling
// a page section. DESIGN-TUNABLE.
const EMPTY_SM_CLASS = "gap-3 px-4 py-6 md:py-6";

/**
 * The one empty state every widget body uses. A title-only `<Empty>` reads as
 * "broken", so this always carries a glyph and a one-line reason. Sized `sm` to
 * sit inside a widget; the maximized/full view can pass `size="default"`. Pass
 * `action` for a CTA.
 */
export function WidgetEmpty({
  icon = "inbox",
  title,
  description,
  size = "sm",
  action,
}: Omittable<{
  icon?: IconName;
  title: string;
  description?: string;
  size?: "sm" | "default";
  action?: ReactNode;
}>) {
  return (
    <Box fill align="center" justify="center">
      <Empty className={size === "sm" ? EMPTY_SM_CLASS : undefined}>
        <EmptyHeader>
          {/* `circle` — the state sits INSIDE a card; the `icon` chip art
              would render card chrome on card chrome. Title at body scale:
              a widget empty is a message, not a page headline. */}
          <EmptyMedia variant="circle">
            <Icon name={icon} size="md" />
          </EmptyMedia>
          <EmptyTitle className="text-base">{title}</EmptyTitle>
          {description ? (
            <EmptyDescription className="max-w-[280px]">{description}</EmptyDescription>
          ) : null}
        </EmptyHeader>
        {action ? <EmptyContent>{action}</EmptyContent> : null}
      </Empty>
    </Box>
  );
}
